import React, { useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../hooks/useAuth';
import { useUniversity } from '../../contexts/UniversityContext';
import {FaLock, FaEye, FaEyeSlash, FaUser } from 'react-icons/fa';
import { toast } from 'react-toastify';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  padding: 0 20px;
  
  @media (max-width: 480px) {
    max-width: 100%;
    padding: 0 5px;
  }
  
  @media (min-width: 768px) and (max-height: 800px) {
    max-width: 360px;
    padding: 0 10px;
  }
  
  @media (min-width: 1024px) and (max-height: 800px) {
    max-width: 100%;
    padding: 0;
  }
`;

const WelcomeMessage = styled.div`
  text-align: center;
  margin-bottom: 20px;
  
  h3 {
    color: ${props => props.theme.text};
    margin: 0 0 8px 0;
    font-size: clamp(16px, 4vw, 18px);
  }
  
  p {
    color: ${props => props.theme.textSecondary};
    margin: 0;
    font-size: clamp(12px, 3vw, 14px);
  }
  
  @media (max-width: 480px) {
    margin-bottom: 12px;
    
    h3 {
      margin-bottom: 4px;
      font-size: clamp(15px, 3.8vw, 16px);
    }
    
    p {
      font-size: clamp(11px, 2.8vw, 12px);
    }
  }
  
  @media (min-width: 768px) and (max-height: 800px) {
    margin-bottom: 15px;
  }
`;

const InputWrapper = styled.div`
  position: relative;
  margin-bottom: 15px;
  
  svg {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: ${props => props.theme.textSecondary};
    font-size: clamp(14px, 3.5vw, 18px);
    z-index: 1;
    
    @media (max-width: 480px) {
      left: 8px;
      font-size: clamp(13px, 3.2vw, 14px);
    }
  }
  
  @media (max-width: 480px) {
    margin-bottom: 10px;
  }
  
  @media (min-width: 768px) and (max-height: 800px) {
    margin-bottom: 12px;
  }
`;

const Input = styled.input`
  padding: 12px 40px 12px 40px;
  width: 100%;
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  background-color: ${props => props.theme.inputBg};
  color: ${props => props.theme.text};
  transition: all 0.2s;
  box-sizing: border-box;
  -webkit-appearance: none;
  appearance: none;
  height: 46px;
  /* 16px es el tamaño que previene zoom en iOS */
  font-size: 16px;
  
  &:focus {
    border-color: ${props => props.theme.primary};
    box-shadow: 0 0 0 2px ${props => props.theme.primary}33;
    outline: none;
  }
  
  &::placeholder {
    color: ${props => props.theme.textSecondary};
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    font-size: 14px;
    
    @media (max-width: 480px) {
      font-size: 13px;
    }
    
    @media (max-width: 350px) {
      font-size: 12px;
    }
  }
  
  @media (max-width: 480px) {
    padding: 8px 30px 8px 30px;
    height: 38px;
    border-radius: 6px;
  }
  
  @media (max-width: 320px) {
    padding: 6px 28px 6px 28px;
    height: 36px;
    border-radius: 5px;
  }
  
  @media (min-width: 768px) and (max-height: 800px) {
    height: 42px;
    padding: 10px 40px 10px 40px;
  }
`;

const Button = styled.button`
  padding: 12px;
  background-color: ${props => props.theme.primary};
  color: white;
  border: none;
  border-radius: 5px;
  font-size: clamp(14px, 3.5vw, 16px);
  font-weight: bold;
  cursor: pointer;
  margin-top: 10px;
  height: 46px;
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  
  &:hover:not(:disabled) {
    background-color: ${props => props.theme.primaryHover};
  }
  
  @media (max-width: 480px) {
    padding: 8px;
    margin-top: 5px;
    height: 38px;
    font-size: 13px;
    border-radius: 4px;
  }
  
  @media (min-width: 768px) and (max-height: 800px) {
    height: 42px;
    margin-top: 8px;
  }
`;

const ForgotPasswordLink = styled.a`
  color: ${props => props.theme.primary};
  font-size: clamp(12px, 3vw, 14px);
  text-align: center;
  cursor: pointer;
  margin-top: 15px;
  
  &:hover {
    text-decoration: underline;
  }
  
  @media (max-width: 480px) {
    margin-top: 10px;
    font-size: clamp(11px, 2.8vw, 12px);
  }
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: ${props => props.theme.textSecondary};
  padding: 4px;
  display: flex;
  align-items: center;
  z-index: 2;
  
  &:hover {
    color: ${props => props.theme.text};
  }
  
  svg {
    position: static;
    transform: none;
    left: auto;
    font-size: clamp(14px, 3.5vw, 16px);
  }
  
  @media (max-width: 480px) {
    right: 8px;
    padding: 3px;
    
    svg {
      font-size: clamp(12px, 3vw, 14px);
    }
  }
`;

const PasswordInputWrapper = styled(InputWrapper)`
  svg:first-child {
    z-index: 1;
  }
`;

const LoginForm = ({ onForgotPassword, onClose }) => {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const formId = React.useMemo(() => `login-form-${Math.random().toString(36).substr(2, 9)}`, []);
  
  const { login } = useAuth();
  const { changeUniversity } = useUniversity();
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!identifier.trim() || !password) {
      toast.error('Por favor, completa todos los campos.');
      return;
    }
    
    setLoading(true);
    
    const isEmail = identifier.includes('@');
    const loginData = isEmail 
      ? { email: identifier, password }
      : { username: identifier, password };
    
    try {
      const result = await login(loginData);
      
      if (result.success) {
        // Make sure to update university immediately after login
        if (result.user && result.user.university) {
          changeUniversity(result.user.university);
        }
        
        // Primero actualizamos el caché de hashtags
        if (window.hashtagsCache) {
          const currentUniversity = localStorage.getItem('selectedUniversity');
          try {
            await window.hashtagsCache.forceRefresh(currentUniversity);
          } catch (err) {
            console.error("Error recargando hashtags:", err);
          }
        }
        
        // Mensaje de éxito opcional
        toast.success('¡Inicio de sesión exitoso!');
        
        // Luego cerramos el modal
        onClose();
      } else {
        toast.error(result.message || 'Error al iniciar sesión. Verifica tus credenciales.');
        setLoading(false);
      }
    } catch (error) {
      toast.error('Error al iniciar sesión. Por favor, intenta de nuevo.');
      setLoading(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <WelcomeMessage>
        <h3>¡El chisme te espera! 👀</h3>
        <p>Inicia sesión para ver todo lo que está pasando</p>
      </WelcomeMessage>
      
      <InputWrapper>
        <FaUser />
        <Input 
          type="text"
          placeholder="Tu correo o usuario secreto"
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value.toLowerCase().trim())}
          disabled={loading}
          id={`${formId}-identifier`}
          name="login-identifier"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck="false"
          autoComplete="off"
        />
      </InputWrapper>
      
      <PasswordInputWrapper>
        <FaLock />
        <Input 
          type={showPassword ? "text" : "password"}
          placeholder="Tu contraseña secreta"
          value={password}
          onChange={(e) => setPassword(e.target.value.trim())}
          disabled={loading}
          id={`${formId}-password`}
          name="login-password"
          autoComplete="off"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck="false"
        />
        <PasswordToggle
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          title={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
          disabled={loading}
        >
          {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
        </PasswordToggle>
      </PasswordInputWrapper>
      
      <Button type="submit" disabled={loading}>
        {loading ? 'Entrando al chisme...' : 'Entrar al chisme'}
      </Button>
      
      <ForgotPasswordLink onClick={onForgotPassword}>
        ¿Olvidaste tu contraseña? 🤔
      </ForgotPasswordLink>
    </Form>
  );
};

export default LoginForm;